<template>
  <teleport to="body">
    <div v-if="visible" class="viewer-overlay informe-print-target">

      <!-- ── Barra superior ── -->
      <div class="viewer-toolbar">
        <div class="toolbar-izq">
          <span class="doc-titulo">{{ informe.titulo ?? 'Informe' }}</span>
          <span class="badge-estado" :class="informe.estado">{{ etiquetaEstado }}</span>
        </div>
        <div class="toolbar-centro">
          <div class="modos">
            <button
              v-for="m in modos"
              :key="m.id"
              class="modo-btn"
              :class="{ activo: modoVista === m.id }"
              @click="modoVista = m.id"
            >{{ m.label }}</button>
          </div>
        </div>
        <div class="toolbar-der">
          <span class="pag-conteo">{{ paginas.length }} sección{{ paginas.length !== 1 ? 'es' : '' }}</span>
          <button class="btn-word" :disabled="generandoWord" @click="descargarWord">
            {{ generandoWord ? 'Generando Word…' : 'Descargar Word' }}
          </button>
          <button class="btn-pdf" :disabled="generandoPdf" @click="imprimirPdf">
            {{ generandoPdf ? 'Preparando…' : 'Descargar PDF' }}
          </button>
          <button class="btn-cerrar-doc" @click="emit('cerrar')">Cerrar</button>
        </div>
      </div>

      <!-- ── Área de visualización ── -->
      <div class="viewer-body">
        <div ref="paginasAreaRef" class="paginas-area" :class="`modo-${modoVista}`">

          <div
            v-for="(fila, fi) in filasAMostrar"
            :key="fi"
            class="fila-paginas"
          >
            <div v-for="pag in fila" :key="pag.id" class="pagina" :data-pagina-id="pag.id">

              <!-- PORTADA -->
              <div v-if="pag.tipo === 'portada'" class="portada">
                <div class="portada-cuerpo">
                  <p class="portada-etiqueta">Informe de Autoevaluación</p>
                  <h1 class="portada-h1">{{ informe.titulo ?? '—' }}</h1>
                  <span class="badge-estado portada-badge" :class="informe.estado">{{ etiquetaEstado }}</span>
                </div>
                <div class="portada-banda-bottom"></div>
              </div>

              <!-- SECCIÓN GENERAL -->
              <div v-else-if="pag.tipo === 'general'" class="pag-body">
                <h2 class="pag-titulo">{{ pag.titulo }}</h2>
                <div class="pag-divider"></div>
                <div v-if="bloquesFor(`gen-${pag.campo}`).length" class="pag-bloques">
                  <BloqueDatos
                    v-for="b in bloquesFor(`gen-${pag.campo}`)"
                    :key="b.id"
                    :tipo="b.tipo"
                    :params="b.params ?? {}"
                    :modoPreview="true"
                  />
                </div>
                <div
                  v-if="textoGeneral(pag.campo) && !tieneTextoBloque(`gen-${pag.campo}`)"
                  class="pag-texto ql-content"
                  v-html="textoGeneral(pag.campo)"
                ></div>
                <p v-else-if="!bloquesFor(`gen-${pag.campo}`).length && !textoGeneral(pag.campo)" class="pag-vacio">
                  Sin contenido redactado.
                </p>
              </div>

              <!-- FACTOR -->
              <div v-else-if="pag.tipo === 'factor' && pag.factor" class="pag-body">
                <p class="pag-eyebrow">Factor</p>
                <h2 class="pag-titulo factor-titulo">{{ pag.factor.nombre }}</h2>
                <div class="pag-divider factor-divider"></div>

                <!-- Sub-secciones -->
                <template v-for="bf in bloquesFactor" :key="bf.clave">
                  <div
                    v-if="bloquesFor(`comp-${pag.factor.id}-${bf.clave}`).length || redaccionHtml(pag.factor.id, bf.clave)"
                    class="sub-sec"
                  >
                    <h3 class="sub-titulo">{{ bf.titulo }}</h3>
                    <div v-if="bloquesFor(`comp-${pag.factor.id}-${bf.clave}`).length" class="pag-bloques">
                      <BloqueDatos
                        v-for="b in bloquesFor(`comp-${pag.factor.id}-${bf.clave}`)"
                        :key="b.id"
                        :tipo="b.tipo"
                        :params="b.params ?? {}"
                        :modoPreview="true"
                      />
                    </div>
                    <div
                      v-if="redaccionHtml(pag.factor.id, bf.clave) && !tieneTextoBloque(`comp-${pag.factor.id}-${bf.clave}`)"
                      class="pag-texto ql-content"
                      v-html="redaccionHtml(pag.factor.id, bf.clave)"
                    ></div>
                  </div>
                </template>

                <!-- Características -->
                <template v-if="pag.factor.hijos.length">
                  <div class="caract-header">Características</div>
                  <div v-for="c in pag.factor.hijos" :key="c.id" class="caract-bloque">
                    <h3 class="caract-titulo">{{ c.nombre }}</h3>
                    <div v-if="bloquesFor(`comp-${c.id}-analisis`).length" class="pag-bloques">
                      <BloqueDatos
                        v-for="b in bloquesFor(`comp-${c.id}-analisis`)"
                        :key="b.id"
                        :tipo="b.tipo"
                        :params="b.params ?? {}"
                        :modoPreview="true"
                      />
                    </div>
                    <div
                      v-if="redaccionHtml(c.id, 'analisis') && !tieneTextoBloque(`comp-${c.id}-analisis`)"
                      class="pag-texto ql-content"
                      v-html="redaccionHtml(c.id, 'analisis')"
                    ></div>
                    <p v-else-if="!bloquesFor(`comp-${c.id}-analisis`).length && !redaccionHtml(c.id, 'analisis')" class="pag-vacio small">
                      Sin análisis redactado.
                    </p>
                  </div>
                </template>
              </div>

              <!-- Pie de página (solo visual en pantalla) -->
              <div class="pagina-pie">
                <span class="pie-titulo">{{ informe.titulo ?? 'Informe' }}</span>
              </div>
            </div>
          </div>

        </div>
      </div>

    </div>
  </teleport>
</template>

<script setup lang="ts">
/**
 * Previsualización del informe antes de exportarlo.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import BloqueDatos from '@/components/BloqueDatos.vue'
import type { Informe } from '@/services/InformeService'

interface NodoArbol { id: number; nombre: string; hijos: Array<{ id: number; nombre: string }> }
interface BloqueDesc { id: string; tipo: string; params?: Record<string, any> }

interface Pagina {
  id: string
  tipo: 'portada' | 'general' | 'factor'
  titulo?: string
  campo?: string
  factor?: NodoArbol
}

const props = defineProps<{
  visible: boolean
  informe: Informe
  factores: NodoArbol[]
  autoImprimir?: boolean
  autoDescargarWord?: boolean
}>()

const emit = defineEmits<{ (e: 'cerrar'): void }>()

const modoVista = ref<'simple' | 'doble'>('simple')

const modos: Array<{ id: 'simple' | 'doble'; label: string }> = [
  { id: 'simple', label: 'Una página' },
  { id: 'doble',  label: 'Dos páginas' },
]

const generales = [
  { campo: 'presentacion',         titulo: 'Presentación' },
  { campo: 'descripcion_programa', titulo: 'Descripción del programa' },
  { campo: 'analisis_resultados',  titulo: 'Análisis de resultados' },
  { campo: 'conclusion',           titulo: 'Conclusión' },
]

const bloquesFactor = [
  { clave: 'conclusiones',             titulo: 'Conclusiones' },
  { clave: 'fortalezas_edit',          titulo: 'Fortalezas' },
  { clave: 'debilidades_edit',         titulo: 'Debilidades' },
  { clave: 'acciones_fortalecimiento', titulo: 'Acciones de fortalecimiento' },
  { clave: 'acciones_mejoramiento',    titulo: 'Acciones de mejoramiento' },
  { clave: 'anexos',                   titulo: 'Anexos' },
]

const etiquetaEstado = computed(() =>
  ({ borrador: 'Borrador', en_revision: 'En revisión', avalado: 'Avalado' }[props.informe.estado ?? 'borrador'])
)

function storageKey(skey: string): string {
  return `informe-bloques-${props.informe.id_informe}-${skey}`
}

function bloquesFor(skey: string): BloqueDesc[] {
  try {
    const raw = localStorage.getItem(storageKey(skey))
    return raw ? JSON.parse(raw) : []
  } catch { return [] }
}

function tieneTextoBloque(skey: string): boolean {
  return bloquesFor(skey).some(b => b.tipo === 'TEXTO')
}

function textoGeneral(campo?: string): string | null {
  if (!campo) return null
  return (props.informe as Record<string, any>)[campo] ?? null
}

function redaccionHtml(idComponente: number, bloque: string): string | null {
  const r = props.informe.redacciones?.find(
    x => x.id_componente === idComponente && x.bloque === bloque
  )
  return r?.contenido ?? null
}

function buildPaginas(): Pagina[] {
  const result: Pagina[] = []
  result.push({ id: 'portada', tipo: 'portada' })
  for (const g of generales) {
    result.push({ id: `gen-${g.campo}`, tipo: 'general', titulo: g.titulo, campo: g.campo })
  }
  for (const f of props.factores) {
    result.push({ id: `factor-${f.id}`, tipo: 'factor', factor: f })
  }
  return result
}

const paginas = ref<Pagina[]>(buildPaginas())
const generandoPdf = ref(false)
const generandoWord = ref(false)
const paginasAreaRef = ref<HTMLElement | null>(null)

watch(() => props.visible, (newVal) => {
  if (newVal) paginas.value = buildPaginas()
})

// Impresion Nativa
function limpiarImpresion(): void {
  document.body.classList.remove('printing-informe')
  generandoPdf.value = false
}

async function imprimirPdf(): Promise<void> {
  if (generandoPdf.value) return
  generandoPdf.value = true

  // El modo simple garantiza ancho de hoja completo al imprimir
  const prevModo = modoVista.value
  modoVista.value = 'simple'

  document.body.classList.add('printing-informe')

  // Al cerrarse el diálogo de impresión, limpiamos el estado
  const onAfter = () => {
    limpiarImpresion()
    modoVista.value = prevModo
    window.removeEventListener('afterprint', onAfter)
  }
  window.addEventListener('afterprint', onAfter)

  // Espera para que el DOM aplique la clase y los bloques se rendericen antes de imprimir
  await new Promise(r => setTimeout(r, 300))
  window.print()
  setTimeout(() => {
    if (generandoPdf.value) {
      limpiarImpresion()
      modoVista.value = prevModo
      window.removeEventListener('afterprint', onAfter)
    }
  }, 1000)
}

// ── Exportación Word ──────────────────────────────────────────────

function svgAPngBuffer(
  svg: SVGSVGElement
): Promise<{ data: ArrayBuffer; width: number; height: number } | null> {
  return new Promise(resolve => {
    const rect = svg.getBoundingClientRect()
    const w = Math.max(rect.width, 300)
    const h = Math.max(rect.height, 150)
    const raw = new XMLSerializer().serializeToString(svg)
    const ns = raw.includes('xmlns=') ? raw : raw.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"')
    const blob = new Blob([ns], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => {
      const scale = 2
      const canvas = document.createElement('canvas')
      canvas.width = w * scale; canvas.height = h * scale
      const ctx = canvas.getContext('2d')!
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.scale(scale, scale); ctx.drawImage(img, 0, 0, w, h)
      URL.revokeObjectURL(url)
      canvas.toBlob(b => {
        if (!b) { resolve(null); return }
        b.arrayBuffer()
          .then(buf => resolve({ data: buf, width: Math.round(w), height: Math.round(h) }))
          .catch(() => resolve(null))
      }, 'image/png')
    }
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null) }
    img.src = url
  })
}

async function descargarWord(): Promise<void> {
  if (generandoWord.value) return
  generandoWord.value = true
  const prevModo = modoVista.value
  modoVista.value = 'simple'
  await new Promise(r => setTimeout(r, 300))

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const D = await import('docx') as any
    const {
      Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
      ImageRun, HeadingLevel, AlignmentType, WidthType, BorderStyle, ShadingType,
    } = D

    const area = paginasAreaRef.value
    if (!area) return

    // ── helpers ──────────────────────────────────────────────

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nodosARuns = (el: Node, fmt: { bold?: boolean; italics?: boolean } = {}): any[] => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const runs: any[] = []
      for (const n of Array.from(el.childNodes)) {
        if (n.nodeType === Node.TEXT_NODE) {
          const text = n.textContent ?? ''
          if (text) runs.push(new TextRun({ text, ...fmt, size: 22, font: 'Calibri' }))
        } else if (n.nodeType === Node.ELEMENT_NODE) {
          const tag = (n as Element).tagName.toUpperCase()
          const f = { ...fmt }
          if (['STRONG', 'B'].includes(tag)) f.bold = true
          if (['EM', 'I'].includes(tag)) f.italics = true
          runs.push(...nodosARuns(n, f))
        }
      }
      return runs
    }

    const cellFill = (cls: string, trCls: string): string => {
      const all = cls + ' ' + trCls
      if (all.includes('celda-factor'))     return 'f5f9fc'
      if (all.includes('fila-subtotal'))    return 'eaf5e9'
      if (all.includes('fila-global'))      return 'dcefff'
      if (all.includes('fila-caract-head')) return 'f5f5f5'
      if (all.includes('clas-fuerte'))      return 'd4edda'
      if (all.includes('clas-debil'))       return 'fff3cd'
      if (all.includes('clas-muy_debil'))   return 'f8d7da'
      return 'ffffff'
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tablaADocx = (tbl: HTMLTableElement): any => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rows: any[] = []
      for (const tr of Array.from(tbl.querySelectorAll('tr'))) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const cells: any[] = []
        const trCls = (tr as HTMLElement).className
        for (const cell of Array.from(tr.querySelectorAll('th,td'))) {
          const el = cell as HTMLElement
          const isHeader = el.tagName === 'TH'
          const colspan = parseInt(el.getAttribute('colspan') ?? '1')
          const rowspan = parseInt(el.getAttribute('rowspan') ?? '1')
          const fill  = isHeader ? '0e69a0' : cellFill(el.className, trCls)
          const color = isHeader ? 'ffffff' : '1a1a1a'
          const bold  = isHeader
            || trCls.includes('fila-subtotal') || trCls.includes('fila-global')
            || el.className.includes('celda-factor')
          const right = el.className.includes('col-num')
          cells.push(new TableCell({
            ...(colspan > 1 ? { columnSpan: colspan } : {}),
            ...(rowspan > 1 ? { rowSpan: rowspan }  : {}),
            shading:  { fill, type: ShadingType.CLEAR, color: 'auto' },
            margins:  { top: 60, bottom: 60, left: 100, right: 100 },
            children: [new Paragraph({
              alignment: right ? AlignmentType.RIGHT : AlignmentType.LEFT,
              children:  [new TextRun({ text: el.textContent?.trim() ?? '', bold, color, size: isHeader ? 16 : 18, font: 'Calibri' })],
            })],
          }))
        }
        if (cells.length) rows.push(new TableRow({ children: cells }))
      }
      return new Table({
        rows,
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top:     { style: BorderStyle.SINGLE, size: 1, color: 'dddddd' },
          bottom:  { style: BorderStyle.SINGLE, size: 1, color: 'dddddd' },
          left:    { style: BorderStyle.SINGLE, size: 1, color: 'dddddd' },
          right:   { style: BorderStyle.SINGLE, size: 1, color: 'dddddd' },
          insideH: { style: BorderStyle.SINGLE, size: 1, color: 'dddddd' },
          insideV: { style: BorderStyle.SINGLE, size: 1, color: 'dddddd' },
        },
      })
    }

    const SKIP_TAGS = new Set(['BUTTON', 'INPUT', 'SELECT', 'STYLE', 'SCRIPT', 'NOSCRIPT'])
    const SKIP_CLS  = [
      'pagina-pie', 'controles', 'barra-superior', 'acciones-derecha',
      'portada-banda-bottom', 'portada-badge', 'pag-vacio', 'leyenda',
    ]

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const domADocx = async (el: HTMLElement): Promise<any[]> => {
      const tag = el.tagName?.toUpperCase() ?? ''
      const cls = (el.className as string) ?? ''

      if (SKIP_TAGS.has(tag)) return []
      if (SKIP_CLS.some(c => cls.includes(c))) return []

      // SVG → PNG image
      if (tag === 'SVG') {
        const png = await svgAPngBuffer(el as unknown as SVGSVGElement)
        if (!png) return []
        const MAX_W = 500, MAX_H = 340
        const ratio = Math.min(MAX_W / png.width, MAX_H / png.height, 1)
        return [new Paragraph({
          children: [new ImageRun({
            data: png.data,
            transformation: { width: Math.round(png.width * ratio), height: Math.round(png.height * ratio) },
            type: 'png',
          })],
          spacing: { after: 120 },
        })]
      }

      // TABLE → docx Table
      if (tag === 'TABLE') {
        return [tablaADocx(el as HTMLTableElement), new Paragraph({ text: '', spacing: { after: 80 } })]
      }

      // Headings
      if (tag === 'H1' || cls.includes('portada-h1')) {
        return [new Paragraph({
          children: [new TextRun({ text: el.textContent?.trim() ?? '', bold: true, size: 52, font: 'Calibri', color: '1a1a1a' })],
          spacing: { before: 400, after: 200 },
        })]
      }
      if (tag === 'H2') {
        return [new Paragraph({ children: nodosARuns(el, { bold: true }), heading: HeadingLevel.HEADING_1 })]
      }
      if (tag === 'H3') {
        return [new Paragraph({ children: nodosARuns(el, { bold: true }), heading: HeadingLevel.HEADING_2 })]
      }

      // Paragraph
      if (tag === 'P') {
        const text = el.textContent?.trim() ?? ''
        if (!text) return []
        if (cls.includes('pag-eyebrow') || cls.includes('portada-etiqueta')) {
          return [new Paragraph({ children: [new TextRun({ text, bold: true, size: 16, color: '0e69a0', font: 'Calibri' })] })]
        }
        const runs = nodosARuns(el)
        return [new Paragraph({ children: runs.length ? runs : [new TextRun({ text, size: 22, font: 'Calibri' })], spacing: { after: 80 } })]
      }

      // Horizontal divider -> small gap
      if (cls.includes('pag-divider') || cls.includes('factor-divider')) {
        return [new Paragraph({ text: '', spacing: { after: 80 } })]
      }

      // 'Caracteristicas' section label
      if (cls.includes('caract-header')) {
        return [new Paragraph({
          children: [new TextRun({ text: el.textContent?.trim() ?? '', bold: true, size: 18, color: '0e69a0', font: 'Calibri' })],
          spacing: { before: 200, after: 80 },
        })]
      }

      // UL / OL
      if (tag === 'UL') {
        return Array.from(el.querySelectorAll('li')).map(li =>
          new Paragraph({ text: li.textContent?.trim() ?? '', bullet: { level: 0 }, spacing: { after: 40 } })
        )
      }
      if (tag === 'OL') {
        return Array.from(el.querySelectorAll('li')).map((li, i) =>
          new Paragraph({ children: [new TextRun({ text: `${i + 1}. ${li.textContent?.trim() ?? ''}`, size: 22, font: 'Calibri' })], spacing: { after: 40 } })
        )
      }

      // KPI card -> inline label + value (skip child elements individually)
      if (cls.includes('kpi-card')) {
        const label = el.querySelector('.kpi-label')?.textContent?.trim() ?? ''
        const valor = el.querySelector('.kpi-valor')?.textContent?.trim() ?? ''
        return [new Paragraph({ children: [
          new TextRun({ text: label ? label + ': ' : '', size: 18, bold: true, font: 'Calibri' }),
          new TextRun({ text: valor, size: 22, bold: true, color: '0e69a0', font: 'Calibri' }),
        ], spacing: { after: 60 } })]
      }
      if (cls.includes('kpi-label') || cls.includes('kpi-valor')) return []

      // Resumen semaforo card
      if (cls.includes('resumen-card')) {
        const titulo = el.querySelector('.resumen-titulo')?.textContent?.trim() ?? ''
        const filas  = Array.from(el.querySelectorAll('.resumen-fila')).map(f => f.textContent?.trim()).join(', ')
        return [new Paragraph({ children: [
          new TextRun({ text: titulo ? titulo + ': ' : '', bold: true, size: 20, color: '0e69a0', font: 'Calibri' }),
          new TextRun({ text: filas, size: 20, font: 'Calibri' }),
        ], spacing: { after: 60 } })]
      }
      if (cls.includes('resumen-titulo') || cls.includes('resumen-fila')) return []

      // Default: recurse into children
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result: any[] = []
      for (const child of Array.from(el.children)) {
        result.push(...await domADocx(child as HTMLElement))
      }
      return result
    }

    // ── Build document ────────────────────────────────────────────

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const docChildren: any[] = []
    const pagElems = Array.from(area.querySelectorAll<HTMLElement>('[data-pagina-id]'))

    for (let i = 0; i < pagElems.length; i++) {
      if (i > 0) docChildren.push(new Paragraph({ pageBreakBefore: true, text: '' }))
      docChildren.push(...await domADocx(pagElems[i]))
    }

    const doc = new Document({
      styles: {
        default: {
          heading1: { run: { size: 30, bold: true, color: '0e69a0', font: 'Calibri' }, paragraph: { spacing: { before: 200, after: 100 } } },
          heading2: { run: { size: 22, bold: true, color: '1a56a0', font: 'Calibri' }, paragraph: { spacing: { before: 160, after: 80  } } },
          document:  { run: { size: 22, font: 'Calibri', color: '1a1a1a' }, paragraph: { spacing: { after: 80 } } },
        },
      },
      sections: [{
        properties: { page: { margin: { top: 1080, right: 1080, bottom: 1080, left: 1080 } } },
        children: docChildren,
      }],
    })

    const blob = await Packer.toBlob(doc)
    const nombre = (props.informe.titulo ?? 'Informe').replace(/[<>:"/\\|?*]/g, '').trim()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `${nombre || 'Informe'}.docx`
    document.body.appendChild(a); a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

  } catch (e) {
    console.error('[Word] Error generando documento:', e)
  } finally {
    generandoWord.value = false
    modoVista.value = prevModo
  }
}

onMounted(() => {
  if (props.visible) {
    paginas.value = buildPaginas()
    if (props.autoImprimir) {
      setTimeout(() => imprimirPdf(), 100)
    }
    if (props.autoDescargarWord) {
      setTimeout(() => descargarWord(), 100)
    }
  }
})

onBeforeUnmount(() => {
  limpiarImpresion()
})

const filasAMostrar = computed<Pagina[][]>(() => {
  if (modoVista.value === 'simple') {
    return paginas.value.map(p => [p])
  }
  const filas: Pagina[][] = []
  for (let i = 0; i < paginas.value.length; i += 2) {
    const fila: Pagina[] = [paginas.value[i]]
    if (paginas.value[i + 1]) fila.push(paginas.value[i + 1])
    filas.push(fila)
  }
  return filas
})
</script>

<style scoped>

.viewer-overlay {
  position: fixed; inset: 0; z-index: 3000;
  display: flex; flex-direction: column; overflow: hidden;
  background: #1a2535;
}

.viewer-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 1.5rem; height: 52px; flex-shrink: 0;
  background: #0e69a0; border-bottom: 1px solid rgba(0,0,0,0.12);
  gap: 1rem;
}
.toolbar-izq { display: flex; align-items: center; gap: 0.7rem; min-width: 0; flex: 1; }
.doc-titulo {
  font-size: 0.85rem; font-weight: 600; color: rgba(255,255,255,0.85);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 340px;
}
.toolbar-centro { display: flex; justify-content: center; flex: 0 0 auto; }
.toolbar-der { display: flex; align-items: center; gap: 1rem; flex: 1; justify-content: flex-end; }
.pag-conteo { font-size: 0.78rem; color: rgba(255,255,255,0.45); white-space: nowrap; }

.modos {
  display: flex; background: rgba(255,255,255,0.08);
  border-radius: 7px; padding: 3px; gap: 2px;
}
.modo-btn {
  padding: 5px 16px; font-size: 0.78rem; font-weight: 600;
  border: none; border-radius: 5px; cursor: pointer;
  background: transparent; color: rgba(255,255,255,0.55);
  font-family: inherit; transition: all 0.15s; white-space: nowrap;
}
.modo-btn:hover:not(.activo) { color: rgba(255,255,255,0.85); }
.modo-btn.activo { background: #0e69a0; color: white; }

.btn-word {
  padding: 6px 18px; font-size: 0.8rem; font-weight: 600;
  border: 1px solid rgba(255,255,255,0.35); border-radius: 6px;
  background: rgba(255,255,255,0.1); color: white;
  cursor: pointer; font-family: inherit; transition: all 0.15s; white-space: nowrap;
  min-width: 140px; text-align: center;
}
.btn-word:hover:not(:disabled) { background: rgba(255,255,255,0.22); }
.btn-word:disabled { opacity: 0.55; cursor: not-allowed; }

.btn-pdf {
  padding: 6px 18px; font-size: 0.8rem; font-weight: 600;
  border: 1px solid rgba(255,255,255,0.35); border-radius: 6px;
  background: rgba(255,255,255,0.1); color: white;
  cursor: pointer; font-family: inherit; transition: all 0.15s; white-space: nowrap;
  min-width: 130px; text-align: center;
}
.btn-pdf:hover:not(:disabled) { background: rgba(255,255,255,0.22); }
.btn-pdf:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-cerrar-doc {
  padding: 6px 18px; font-size: 0.8rem; font-weight: 600;
  border: 1px solid rgba(255,255,255,0.2); border-radius: 6px;
  background: transparent; color: rgba(255,255,255,0.7);
  cursor: pointer; font-family: inherit; transition: all 0.15s; white-space: nowrap;
}
.btn-cerrar-doc:hover { background: rgba(255,255,255,0.12); color: white; }

.badge-estado {
  display: inline-block; padding: 3px 10px; border-radius: 5px; flex-shrink: 0;
  font-size: 0.67rem; font-weight: 800; letter-spacing: 0.08em;
  text-transform: uppercase; color: white;
}
.badge-estado.borrador    { background: #c47a00; }
.badge-estado.en_revision { background: #0e69a0; }
.badge-estado.avalado     { background: #1a7f1a; }

.viewer-body {
  flex: 1; overflow-y: auto; overflow-x: auto;
  padding: 3rem 2rem 4rem;
  scrollbar-color: rgba(255,255,255,0.15) transparent;
}
.viewer-body::-webkit-scrollbar { width: 8px; }
.viewer-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 4px; }

.paginas-area {
  display: flex; flex-direction: column; align-items: center; gap: 3rem;
}

.fila-paginas {
  display: flex; gap: 3rem; justify-content: center; align-items: flex-start;
}

.pagina {
  background: white;
  box-shadow: 0 6px 32px rgba(0,0,0,0.45), 0 1px 4px rgba(0,0,0,0.3);
  display: flex; flex-direction: column;
  position: relative; flex-shrink: 0;
  width: 794px;
  min-height: 1123px;
}

.paginas-area.modo-doble .pagina {
  width: 560px;
  min-height: 792px;
  font-size: 0.78rem;
}

.pag-body {
  flex: 1; padding: 72px 80px 40px;
}

.paginas-area.modo-doble .pag-body {
  padding: 52px 58px 32px;
}

.portada {
  flex: 1; display: flex; flex-direction: column;
}
.portada-cuerpo {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 60px 80px; text-align: center; gap: 1.25rem;
}
.portada-etiqueta {
  font-size: 0.8rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.15em; color: #0e69a0; margin: 0;
}
.portada-h1 {
  font-size: 2rem; font-weight: 800; color: #1a1a1a;
  line-height: 1.25; margin: 0; text-wrap: balance;
}
.portada-badge {
  margin-top: 0.5rem;
}
.portada-banda-bottom {
  height: 4px; background: #0e69a0; opacity: 0.3;
}

.paginas-area.modo-doble .portada-h1 { font-size: 1.4rem; }
.paginas-area.modo-doble .portada-cuerpo { padding: 40px 56px; }

.pag-eyebrow {
  font-size: 0.68rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.12em; color: #0e69a0; margin: 0 0 0.3rem;
}
.pag-titulo {
  font-size: 1.25rem; font-weight: 800; color: #1a1a1a;
  margin: 0 0 0.75rem; line-height: 1.2; text-wrap: balance;
}
.factor-titulo { color: #0e69a0; }

.pag-divider {
  height: 2px; background: #e0e0e0; margin-bottom: 1.5rem;
}
.factor-divider { background: #0e69a0; opacity: 0.35; }

.pag-texto {
  font-size: 0.9em; line-height: 1.8; color: #333; margin-top: 0.5rem;
}
.pag-vacio {
  color: #bbb; font-style: italic; font-size: 0.82em; margin: 0;
}
.pag-vacio.small { font-size: 0.78em; }

.pag-bloques {
  display: flex; flex-direction: column; gap: 1rem; margin-bottom: 0.75rem;
}

.sub-sec {
  margin-top: 1.25rem; padding-top: 1rem;
  border-top: 1px solid #ececec;
}
.sub-titulo {
  font-size: 0.88em; font-weight: 700; color: #333;
  margin: 0 0 0.5rem; text-transform: uppercase; letter-spacing: 0.04em;
}

.caract-header {
  margin-top: 1.5rem; margin-bottom: 0.75rem;
  font-size: 0.7em; font-weight: 800; text-transform: uppercase;
  letter-spacing: 0.12em; color: #0e69a0;
  padding-bottom: 0.4rem; border-bottom: 2px solid #dce8f0;
}
.caract-bloque {
  margin-bottom: 1rem; padding: 0.75rem; background: #f8fbfe;
  border-left: 3px solid #0e69a0; border-radius: 0 4px 4px 0;
}
.caract-titulo {
  font-size: 0.88em; font-weight: 700; color: #0e69a0;
  margin: 0 0 0.4rem;
}

.pagina-pie {
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 80px; border-top: 1px solid #e8e8e8;
  margin-top: auto; flex-shrink: 0;
}
.pie-titulo {
  font-size: 0.68em; color: #bbb;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 60%;
}

.paginas-area.modo-doble .pagina-pie { padding: 10px 56px; }

.pag-body :deep(.contenedor-tabla) {
  overflow: visible;
  width: 100%;
  max-width: 100%;
}
.pag-body :deep(.lista-tabla) {
  width: 100%;
  max-width: 100%;
  table-layout: fixed;
  font-size: 0.8em;
  border-collapse: collapse;
}
.pag-body :deep(.lista-tabla th),
.pag-body :deep(.lista-tabla td) {
  min-width: 0;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
  padding: 5px 6px;
  vertical-align: top;
}

.paginas-area.modo-doble .pag-body :deep(.lista-tabla) {
  font-size: 0.7em;
}
.paginas-area.modo-doble .pag-body :deep(.lista-tabla th),
.paginas-area.modo-doble .pag-body :deep(.lista-tabla td) {
  padding: 3px 4px;
}

/* Tabla comparativo: encabezados envuelven para que quepan en la página */
.pag-body :deep(.lista-tabla.comparativo) {
  table-layout: auto;
  font-size: 0.65em;
}
.pag-body :deep(.lista-tabla.comparativo th) {
  white-space: normal;
  word-break: break-word;
  padding: 4px 5px;
  min-width: 36px;
}
.pag-body :deep(.lista-tabla.comparativo td) {
  padding: 4px 5px;
}
.pag-body :deep(.lista-tabla.comparativo td.col-num) {
  white-space: nowrap;
}
.pag-body :deep(.lista-tabla.comparativo .col-nombre) {
  white-space: normal;
  min-width: 90px;
  max-width: 170px;
}

.ql-content :deep(p) { margin: 0 0 0.6em; }
.ql-content :deep(h3), .ql-content :deep(h4) { margin: 0.9em 0 0.4em; font-size: 1em; }
.ql-content :deep(ul), .ql-content :deep(ol) { padding-left: 1.4rem; margin: 0 0 0.6em; }
.ql-content :deep(li) { margin-bottom: 0.2em; }
.ql-content :deep(strong) { font-weight: 700; }
.ql-content :deep(em) { font-style: italic; }
.ql-content :deep(a) { color: #0e69a0; }

.pag-body :deep(.bloque-texto) {
  border: none !important;
  padding: 0 !important;
  box-shadow: none !important;
  font-size: 0.9em;
  line-height: 1.8;
  color: #333;
}
.pag-body :deep(.bloque-texto p)  { margin: 0 0 0.6em; }
.pag-body :deep(.bloque-texto h3),
.pag-body :deep(.bloque-texto h4) { margin: 0.9em 0 0.4em; font-size: 1em; }
.pag-body :deep(.bloque-texto ul),
.pag-body :deep(.bloque-texto ol) { padding-left: 1.4rem; margin: 0 0 0.6em; }
.pag-body :deep(.bloque-texto li)     { margin-bottom: 0.2em; }
.pag-body :deep(.bloque-texto strong) { font-weight: 700; }
.pag-body :deep(.bloque-texto em)     { font-style: italic; }
</style>

<style>
@page {
  size: A4 portrait;
  margin: 1.6cm 1.4cm;
}

@media print {
  body.printing-informe > *:not(.informe-print-target) {
    display: none !important;
  }

  body.printing-informe .informe-print-target {
    position: static !important;
    background: white !important;
    overflow: visible !important;
    z-index: auto !important;
    display: block !important;
  }

  body.printing-informe .viewer-toolbar {
    display: none !important;
  }

  body.printing-informe .viewer-body {
    overflow: visible !important;
    padding: 0 !important;
    flex: none !important;
    display: block !important;
  }

  body.printing-informe .paginas-area {
    display: block !important;
    gap: 0 !important;
    align-items: initial !important;
  }

  body.printing-informe .fila-paginas {
    display: block !important;
    gap: 0 !important;
  }

  body.printing-informe .pagina {
    width: 100% !important;
    min-height: 0 !important;
    height: auto !important;
    box-shadow: none !important;
    border: none !important;
    margin: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;
    display: block !important;
    background: white !important;
    background-image: none !important;
    page-break-after: always !important;
    break-after: page !important;
    overflow: visible !important;
  }

  body.printing-informe .fila-paginas:last-child .pagina:last-child {
    page-break-after: avoid !important;
    break-after: avoid !important;
  }

  body.printing-informe .pag-body {
    padding: 0 !important;
    flex: none !important;
    display: block !important;
  }

  body.printing-informe .portada {
    display: flex !important;
    flex-direction: column !important;
    min-height: 24cm !important;
  }
  body.printing-informe .portada-cuerpo {
    padding: 0 !important;
  }

  body.printing-informe .pagina-pie {
    display: none !important;
  }

  body.printing-informe .kpi-card,
  body.printing-informe .grafico-card,
  body.printing-informe .resumen-card,
  body.printing-informe tr {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  body.printing-informe .contenedor-tabla,
  body.printing-informe .lista-tabla,
  body.printing-informe .lista-tabla tbody,
  body.printing-informe .sub-sec,
  body.printing-informe .caract-bloque,
  body.printing-informe .pag-bloques {
    break-inside: auto !important;
    page-break-inside: auto !important;
    overflow: visible !important;
  }

  body.printing-informe .sub-titulo,
  body.printing-informe .caract-titulo,
  body.printing-informe .caract-header,
  body.printing-informe .pag-titulo {
    break-after: avoid !important;
    page-break-after: avoid !important;
  }

  body.printing-informe .contenedor-tabla {
    width: 100% !important;
    max-width: 100% !important;
  }

  body.printing-informe .lista-tabla {
    width: 100% !important;
    max-width: 100% !important;
    table-layout: fixed !important;
    font-size: 0.75rem !important;
    border-collapse: collapse !important;
  }

  body.printing-informe .lista-tabla th,
  body.printing-informe .lista-tabla td {
    min-width: 0 !important;
    white-space: normal !important;
    word-break: break-word !important;
    overflow-wrap: break-word !important;
    padding: 4px 6px !important;
    vertical-align: top !important;
  }

  body.printing-informe .lista-tabla.comparativo {
    table-layout: auto !important;
    font-size: 0.62rem !important;
  }
  body.printing-informe .lista-tabla.comparativo th {
    white-space: normal !important;
    word-break: break-word !important;
    padding: 2px 4px !important;
  }
  body.printing-informe .lista-tabla.comparativo td {
    padding: 2px 4px !important;
    white-space: normal !important;
  }
  body.printing-informe .lista-tabla.comparativo td.col-num {
    white-space: nowrap !important;
    word-break: normal !important;
  }
  body.printing-informe .lista-tabla.comparativo .col-nombre {
    min-width: 80px !important;
    max-width: 160px !important;
    word-break: break-word !important;
  }

  /* Repetir la cabecera de la tabla en cada hoja */
  body.printing-informe .lista-tabla thead {
    display: table-header-group !important;
  }

  body.printing-informe * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>